import type { LearnArticle } from '~/content/learn';

type OrderableArticle = Pick<
  LearnArticle,
  'category' | 'subcategory' | 'categoryOrder' | 'allArticlesOrder'
>;

/** One block of the /help list: ungrouped articles (`label: null`) or a subcategory. */
export interface HelpArticleGroup<T> {
  label: string | null;
  articles: T[];
}

/**
 * Stable sort on an optional position: numbered articles first (lowest first),
 * then the rest in their incoming order, which is newest first from Payload.
 */
function byPosition<T>(articles: readonly T[], position: (article: T) => number | undefined): T[] {
  return articles
    .map((article, index) => ({ article, index, position: position(article) }))
    .sort((a, b) => {
      if (a.position !== undefined && b.position !== undefined && a.position !== b.position) {
        return a.position - b.position;
      }
      if (a.position !== undefined && b.position === undefined) return -1;
      if (a.position === undefined && b.position !== undefined) return 1;
      return a.index - b.index;
    })
    .map(({ article }) => article);
}

/** "All articles": pinned articles (`allArticlesOrder`) first, then the rest. */
export function orderAllArticles<T extends OrderableArticle>(articles: readonly T[]): T[] {
  return byPosition(articles, (article) => article.allArticlesOrder);
}

/**
 * One category's articles as ordered groups. Ungrouped articles come first,
 * then each subcategory (e.g. "Local tips") under its own heading, with the
 * subcategories ordered by their lowest-positioned article.
 */
export function groupCategoryArticles<T extends OrderableArticle>(
  articles: readonly T[],
  category: string,
): HelpArticleGroup<T>[] {
  const ordered = byPosition(
    articles.filter((article) => article.category.trim() === category),
    (article) => article.categoryOrder,
  );

  const ungrouped: T[] = [];
  const subcategories = new Map<string, T[]>();
  for (const article of ordered) {
    const label = article.subcategory?.trim();
    if (!label) {
      ungrouped.push(article);
      continue;
    }
    const group = subcategories.get(label);
    if (group) group.push(article);
    else subcategories.set(label, [article]);
  }

  const groups: HelpArticleGroup<T>[] = [];
  if (ungrouped.length > 0) groups.push({ label: null, articles: ungrouped });
  for (const [label, group] of subcategories) groups.push({ label, articles: group });
  return groups;
}
